/* Campo de busca por nome — o mesmo controle em todas as listas do painel.

   Nasceu do seletor de turma do ProfessorApp, que já tinha esta forma (ícone +
   input + contador "X de Y"). Virou componente porque o pedido é o mesmo nas
   quatro listas — redes, regiões, escolas e turmas — e a alternativa era copiar
   o bloco quatro vezes, com quatro chances de divergir em placeholder, largura e
   contagem.

   O componente NÃO filtra nada: recebe o termo e devolve o que foi digitado.
   Quem filtra é cada tela, e é isso que faz o controle servir aos dois modos que
   o painel precisa:

     - LOCAL, sobre o que a tela já tem (`filtrarPorNome` de lib/busca.js) —
       redes, regiões e turmas vêm completas;
     - NO SERVIDOR, quando a lista é paginada — o ranking de escolas traz 100 por
       vez de até 990, e filtrar no cliente esconderia da busca tudo o que está
       fora da página. Lá o termo vai como `nome` na chamada.

   Em ambos os casos o recorte de perfil continua sendo do backend: a busca
   estreita o que o usuário já podia ver, nunca alarga. */

import { Search } from "lucide-react";
import PropTypes from "prop-types";

import { cn } from "../lib/utils";

export function BuscaNome({
  id,
  value,
  onChange,
  placeholder,
  total,
  filtrado,
  rotulo,
  rotuloPlural,
  className
}) {
  // O contador só aparece quando a tela sabe os dois números. No modo servidor o
  // total da página nem sempre é conhecido antes da resposta, e um "0 de 0"
  // piscando a cada tecla é pior do que contador nenhum.
  const mostrarContagem =
    typeof total === "number" && typeof filtrado === "number";

  const plural = rotuloPlural || `${rotulo}s`;

  return (
    <div className={cn("flex flex-wrap items-center gap-3", className)}>
      <label
        htmlFor={id}
        className="flex min-w-[240px] flex-1 items-center gap-2 rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface-2)] px-3 py-2 sm:max-w-[340px] sm:flex-none"
      >
        <Search
          size={15}
          className="flex-none text-[var(--color-ink-faint)]"
          aria-hidden
        />
        <input
          id={id}
          type="search"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          // O AVA declara `.hidden { pointer-events: none }` e outras regras
          // globais em input; o painel é escopado, mas o inline garante o foco
          // independente do que o Bootstrap traga por cima.
          style={{ pointerEvents: "auto" }}
          className="w-full border-0 bg-transparent text-[13px] text-[var(--color-ink)] outline-none placeholder:text-[var(--color-ink-faint)]"
        />
      </label>

      {mostrarContagem && (
        <span className="text-[12px] text-[var(--color-ink-faint)]">
          {filtrado === total
            ? `${total} ${total === 1 ? rotulo : plural}`
            : `${filtrado} de ${total} ${total === 1 ? rotulo : plural}`}
        </span>
      )}
    </div>
  );
}

BuscaNome.propTypes = {
  /** id do input — precisa ser único na página, o label aponta para ele. */
  id: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  /** Quantos itens existem antes do filtro. Omitido, esconde o contador. */
  total: PropTypes.number,
  /** Quantos sobraram. Omitido, esconde o contador. */
  filtrado: PropTypes.number,
  /** Nome do item no singular ("escola", "turma", "rede"). */
  rotulo: PropTypes.string.isRequired,
  /** Plural, quando não é só somar "s" ("região" -> "regiões"). */
  rotuloPlural: PropTypes.string,
  className: PropTypes.string
};

BuscaNome.defaultProps = {
  total: undefined,
  filtrado: undefined,
  rotuloPlural: undefined,
  className: "mb-4"
};

/* Mensagem padrão de "a busca não achou nada", para as listas não divergirem.

   `masculino` existe porque a frase concorda em português: as quatro listas do
   painel são femininas (rede, região, escola, turma) e a de alunos não. Deixar a
   concordância como parâmetro é mais honesto do que montar a frase por
   heurística no nome do item. */
export function SemResultadoBusca({ termo, item, masculino }) {
  return (
    <p className="py-6 text-center text-[13px] text-[var(--color-ink-faint)]">
      {`${masculino ? "Nenhum" : "Nenhuma"} ${item} corresponde a `}
      <span className="font-medium text-[var(--color-ink-soft)]">
        “{termo}”
      </span>
      .
    </p>
  );
}

SemResultadoBusca.propTypes = {
  termo: PropTypes.string.isRequired,
  /** Item no singular: "escola", "turma", "rede", "região". */
  item: PropTypes.string.isRequired,
  masculino: PropTypes.bool
};

SemResultadoBusca.defaultProps = { masculino: false };
